// ===========================================
// components/UploadTab.js
// ===========================================
import React, { useState } from 'react';
import { Upload, Book, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import config from '../utils/config';

const UploadTab = ({ user, onUpload }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', or null
  const [uploadResults, setUploadResults] = useState([]); // Track individual file results
  const [bookDetails, setBookDetails] = useState({
    title: '',
    author: '',
    description: ''
  });

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setUploadStatus(null); // Clear any previous status
    setUploadResults([]); // Clear previous results
    // Reset book details when new files are selected
    if (files.length === 1) {
      setBookDetails({
        title: files[0].name.replace('.pdf', ''),
        author: '',
        description: ''
      });
    } else {
      setBookDetails({
        title: '',
        author: '',
        description: ''
      });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    console.log('Starting upload process...');
    console.log('Selected files:', selectedFiles.map(f => f.name));
    console.log('API URL:', config.getApiUrl());
    console.log('Auth token exists:', !!localStorage.getItem('access_token'));

    setUploading(true);
    setUploadStatus(null);
    setUploadResults([]);
    let successCount = 0;
    let errorCount = 0;
    const results = [];
    
    for (const file of selectedFiles) {
      try {
        console.log(`Uploading file: ${file.name} (${file.size} bytes)`);
        
        const formData = new FormData();
        formData.append('file', file);
        
        // Use bookDetails for single file or fallback for multiple files
        if (selectedFiles.length === 1) {
          formData.append('title', bookDetails.title || file.name.replace('.pdf', ''));
          formData.append('author', bookDetails.author || 'Unknown Author');
          formData.append('description', bookDetails.description || 'Uploaded book');
        } else {
          formData.append('title', file.name.replace('.pdf', ''));
          formData.append('author', 'Unknown Author');
          formData.append('description', 'Uploaded book');
        }

        console.log('FormData contents:', {
          title: formData.get('title'),
          author: formData.get('author'),
          description: formData.get('description'),
          fileName: file.name
        });

        const result = await api.uploadBook(formData);
        console.log('Upload successful:', result);
        successCount++;
        results.push({ filename: file.name, status: 'success' });
      } catch (error) {
        console.error('Upload failed for', file.name, ':', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        errorCount++;
        results.push({ filename: file.name, status: 'error', error: error.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    setSelectedFiles([]);
    setBookDetails({
      title: '',
      author: '',
      description: ''
    });

    // Set status based on results
    if (errorCount === 0) {
      setUploadStatus('success');
      // Refresh books list only if some uploads were successful
      if (onUpload && successCount > 0) {
        onUpload();
      }
    } else if (successCount === 0) {
      setUploadStatus('error');
    } else {
      setUploadStatus('partial');
      // Refresh books list if there were some successful uploads
      if (onUpload && successCount > 0) {
        onUpload();
      }
    }

    // Clear status after 5 seconds
    setTimeout(() => {
      setUploadStatus(null);
      setUploadResults([]);
    }, 5000);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Upload Books</h2>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        {uploadStatus && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            uploadStatus === 'success' 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : uploadStatus === 'error'
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
          }`}>
            {uploadStatus === 'success' && <CheckCircle className="h-5 w-5" />}
            {uploadStatus === 'error' && <XCircle className="h-5 w-5" />}
            {uploadStatus === 'partial' && <AlertCircle className="h-5 w-5" />}
            <span>
              {uploadStatus === 'success' && 'Books uploaded successfully!'}
              {uploadStatus === 'error' && 'Upload failed. Please try again.'}
              {uploadStatus === 'partial' && 'Some books uploaded successfully, others failed.'}
            </span>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select PDF Files
          </label>
          <input
            type="file"
            multiple
            accept=".pdf"
            onChange={handleFileSelect}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h3>
            <ul className="space-y-2">
              {selectedFiles.map((file, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                  <Book className="h-4 w-4" />
                  {file.name}
                </li>
              ))}
            </ul>
          </div>
        )}

        {selectedFiles.length === 1 && (
          <div className="mb-6 space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Book Details:</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={bookDetails.title}
                onChange={(e) => setBookDetails(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter book title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author
              </label>
              <input
                type="text"
                value={bookDetails.author}
                onChange={(e) => setBookDetails(prev => ({ ...prev, author: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter author name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={bookDetails.description}
                onChange={(e) => setBookDetails(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Enter book description"
              />
            </div>
          </div>
        )}

        {uploadResults.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Upload Results:</h3>
            <div className="space-y-2">
              {uploadResults.map((result, index) => (
                <div key={index} className={`flex items-center gap-2 text-sm p-2 rounded ${
                  result.status === 'success' 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {result.status === 'success' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  <span className="flex-1">{result.filename}</span>
                  {result.status === 'error' && (
                    <span className="text-xs text-red-600">{result.error}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || uploading}
          className={`w-full py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 ${
            selectedFiles.length === 0 || uploading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white`}
        >
          <Upload className="h-5 w-5" />
          {uploading ? 'Uploading...' : 'Upload Books'}
        </button>
      </div>
    </div>
  );
};

export default UploadTab;